import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGoogleCalendarAuth } from '@/hooks/useGoogleCalendarAuth';
import { useUserIcons } from '@/hooks/useUserIcons';
import { IconPicker } from './IconPicker';
import { Loader2, Calendar, Trash2, RefreshCw, Smile } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

interface UserSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  userColor: string;
}

export const UserSettingsDialog = ({
  open,
  onOpenChange,
  userId,
  userName,
  userColor,
}: UserSettingsDialogProps) => {
  const [googleEmail, setGoogleEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { initiateAuth, disconnectCalendar, checkConnectionStatus } = useGoogleCalendarAuth();
  const { getUserIcon, setUserIcon, isSaving } = useUserIcons();
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<{
    isConnected: boolean;
    googleEmail: string | null;
    connectedAt: string | null;
  }>({ isConnected: false, googleEmail: null, connectedAt: null });

  useEffect(() => {
    if (open) {
      loadConnectionStatus();
      setSelectedIcon(getUserIcon(userId));
    }
  }, [open, userId]);

  const loadConnectionStatus = async () => {
    setIsLoading(true);
    const status = await checkConnectionStatus(userId);
    setConnectionStatus(status);
    setIsLoading(false);
  };

  const handleConnect = () => {
    if (!googleEmail.trim()) {
      return;
    }
    initiateAuth(userId, googleEmail.trim());
    onOpenChange(false);
  };

  const handleDisconnect = async () => {
    setIsLoading(true);
    await disconnectCalendar(userId);
    await loadConnectionStatus();
    setIsLoading(false);
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    await loadConnectionStatus();
    setIsLoading(false);
  };

  const handleIconSelect = async (iconName: string) => {
    setSelectedIcon(iconName);
    await setUserIcon(userId, iconName);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            הגדרות - {userName}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="icon" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-slate-700">
            <TabsTrigger value="icon" className="gap-2 data-[state=active]:bg-slate-600 data-[state=active]:text-slate-100 text-slate-300">
              <Smile className="h-4 w-4" />
              אייקון
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2 data-[state=active]:bg-slate-600 data-[state=active]:text-slate-100 text-slate-300">
              <Calendar className="h-4 w-4" />
              יומן
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="icon" className="mt-4">
            <div className="space-y-3">
              <Label className="text-slate-300">בחר אייקון שייצג אותך</Label>
              <IconPicker
                selectedIcon={selectedIcon}
                onSelectIcon={handleIconSelect}
                userColor={userColor}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="calendar" className="mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
              </div>
            ) : connectionStatus.isConnected ? (
              <div className="space-y-4">
                <div className="rounded-lg bg-green-900/30 border border-green-700 p-4 space-y-2">
                  <p className="text-sm font-medium text-green-300">
                    ✅ מחובר ליומן Google
                  </p>
                  <p className="text-xs text-green-400">
                    {connectionStatus.googleEmail}
                  </p>
                  {connectionStatus.connectedAt && (
                    <p className="text-xs text-green-500">
                      מחובר מאז:{' '}
                      {format(new Date(connectionStatus.connectedAt), 'dd/MM/yyyy HH:mm', {
                        locale: he,
                      })}
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleRefresh}
                    className="flex-1 bg-slate-700 border-slate-600 text-slate-100 hover:bg-slate-600"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    רענן סנכרון
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDisconnect}
                    className="flex-1"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    נתק
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="google-email" className="text-slate-300">כתובת Gmail</Label>
                  <Input
                    id="google-email"
                    type="email"
                    placeholder="example@gmail.com"
                    value={googleEmail}
                    onChange={(e) => setGoogleEmail(e.target.value)}
                    dir="ltr"
                    className="bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400"
                  />
                  <p className="text-xs text-slate-400">
                    הזן את כתובת ה-Gmail שלך כדי לחבר את היומן
                  </p>
                </div>

                <Button
                  onClick={handleConnect}
                  disabled={!googleEmail.trim()}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  חבר יומן Google
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};